import cogoToast from "cogo-toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addUser, setlastLogin } from "../../app/features/counterSlice";
import logo from "../../asstes/asd.png";
import CredentialsStep from "./CredentialsStep";
import OtpStep from "./OtpStep";
import "./authModern.css";

const STEP_EXIT_MS = 140;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const numberOr = (value, fallback) => (Number.isFinite(value) ? value : fallback);

/**
 * Sign-in, in two steps:
 *   1. email + password  -> the server e-mails a 6-digit code        (CredentialsStep)
 *   2. the code          -> only now is the session created          (OtpStep)
 * (If the server has two-step verification switched off, step 1 signs in directly.)
 *
 * This component owns the hand-over between the steps and the session bookkeeping.
 */
const Auth2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState("credentials"); // credentials | otp
  const [direction, setDirection] = useState("none"); // which way the step slides in
  const [leaving, setLeaving] = useState(null); // null, or the direction the step is leaving in
  const [challenge, setChallenge] = useState(null);
  const [email, setEmail] = useState(""); // kept here so it survives a trip to the code step
  const [notice, setNotice] = useState(""); // why the user is back on the credentials step

  const stepTimer = useRef(null);

  useEffect(() => () => clearTimeout(stepTimer.current), []);

  // Exit fast, enter smoothly; skipped entirely for people who asked for less motion.
  const switchStep = useCallback((next, nextDirection) => {
    const apply = () => {
      setStep(next);
      setDirection(nextDirection);
      setLeaving(null);
      if (next === "credentials") setChallenge(null);
    };

    clearTimeout(stepTimer.current);
    if (prefersReducedMotion()) {
      apply();
      return;
    }
    setLeaving(nextDirection);
    stepTimer.current = setTimeout(apply, STEP_EXIT_MS);
  }, []);

  const completeLogin = useCallback(
    (response) => {
      // Same session bookkeeping this page has always done.
      localStorage.setItem("userData", JSON.stringify(response.data));
      localStorage.setItem("token", response.token);
      localStorage.setItem("jkbnm", "nfjsdngj");

      dispatch(addUser({ email: response?.data?.email, id: response?.data?._id }));
      dispatch(setlastLogin(response?.previousLogin));
      cogoToast.success("Logged in successfully", { position: "bottom-right" });
      navigate("/");
    },
    [dispatch, navigate]
  );

  // The password was accepted: the code step is next, or (two-step verification switched off
  // on the server) the user is signed in already. Returns false for anything else.
  const handleSignedIn = useCallback(
    (response) => {
      if (response?.success === "otp_required" && response.challengeId) {
        setChallenge({
          id: response.challengeId,
          email: response.email,
          expiresIn: numberOr(response.expiresIn, 300),
          resendAfter: numberOr(response.resendAfter, 30),
          resendsLeft: numberOr(response.resendsLeft, 3),
          receivedAt: Date.now(),
        });
        switchStep("otp", "forward");
        return true;
      }
      if (response?.success === "true" || response?.success === true) {
        completeLogin(response);
        return true;
      }
      return false;
    },
    [completeLogin, switchStep]
  );

  // Back to the form, optionally with a reason. The password field starts empty again because
  // the form is rebuilt from scratch; only the e-mail address is carried over.
  const leaveOtp = useCallback(
    (message) => {
      setNotice(typeof message === "string" ? message : "");
      switchStep("credentials", "back");
    },
    [switchStep]
  );

  const year = new Date().getFullYear();

  return (
    <main className="mrc-auth">
      <div className="mrc-auth__card">
        <div
          className="mrc-auth__progress"
          aria-hidden="true"
          style={{ "--progress": step === "otp" ? 1 : 0.5 }}
        />

        <span className="mrc-auth__logo">
          <img src={logo} alt="MRC group" draggable="false" />
        </span>

        {step === "otp" && challenge ? (
          <OtpStep
            key={challenge.id}
            challenge={challenge}
            enter={direction}
            leaving={leaving}
            onVerified={completeLogin}
            onBack={leaveOtp}
          />
        ) : (
          <CredentialsStep
            email={email}
            onEmailChange={setEmail}
            notice={notice}
            focusPassword={Boolean(notice)}
            enter={direction}
            leaving={leaving}
            onSignedIn={handleSignedIn}
          />
        )}
      </div>

      <p className="mrc-auth__footer">
        {"Copyright © "}
        <a href="https://www.dcmcloud.com/" target="_blank" rel="noopener noreferrer">
          DCMCloud
        </a>{" "}
        {year}.
      </p>
    </main>
  );
};

export default Auth2;
